import type { CogoBlockDef, CogoFlow } from "../core/types"
import type { ActionFlowEngine } from "./action-flow-engine"
import type { StateManager } from "./state-manager"
import type { CogoInspector } from "@cogo/runtime-v2"

interface ModelContextTool {
  name: string
  description: string
  inputSchema?: Record<string, unknown>
  readOnly?: boolean
  execute(params: Record<string, unknown>): Promise<{
    content: Array<{ type: string; text: string }>
  }>
}

interface ModelContext {
  registerTool(tool: ModelContextTool): void
}

declare global {
  interface Navigator {
    modelContext?: ModelContext
  }
}

/**
 * Auto-register logic block flows as WebMCP tools.
 * Each exposed flow in a logic block becomes a navigator.modelContext tool.
 * When an inspector is provided (dev mode), debug tools are also registered.
 */
export function registerWebMCPTools(
  blocks: Record<string, CogoBlockDef>,
  flows: Record<string, CogoFlow>,
  actionEngine: ActionFlowEngine,
  stateManager: StateManager,
  inspector?: CogoInspector,
): void {
  if (typeof navigator === "undefined" || !navigator.modelContext) return

  const mc = navigator.modelContext

  for (const [, block] of Object.entries(blocks)) {
    const manifest = block.manifest
    if (!manifest) continue
    if (manifest.type === "ui" && !manifest.webmcp?.expose) continue
    if (!manifest.webmcp?.expose) continue

    const ns = manifest.namespace
    if (!ns) continue

    for (const [toolName, toolMeta] of Object.entries(manifest.webmcp.tools)) {
      const flowKey = `${ns}.${toolName}`
      const flow = flows[flowKey]
      if (!flow) continue

      mc.registerTool({
        name: flowKey,
        description: toolMeta.description,
        inputSchema: block.variables?.context
          ? { type: "object", properties: block.variables.context }
          : undefined,
        readOnly: toolMeta.readOnly,

        async execute(params) {
          await actionEngine.executeFlow(flow, params)
          const state = stateManager.get(ns)
          return {
            content: [
              {
                type: "text",
                text: JSON.stringify(state),
              },
            ],
          }
        },
      })
    }
  }

  // Register debug tools when inspector is enabled (dev mode only)
  if (inspector?.enabled) {
    registerDebugTools(mc, inspector)
  }
}

function registerDebugTools(mc: ModelContext, inspector: CogoInspector): void {
  mc.registerTool({
    name: "debug_inspect_state",
    description: "Inspect runtime state — full snapshot or specific dot-path",
    inputSchema: {
      type: "object",
      properties: {
        path: { type: "string", description: "Optional dot-path (e.g., 'appData.cart.items')" },
      },
    },
    readOnly: true,
    async execute(params) {
      const path = params.path as string | undefined
      const result = path ? inspector.getStatePath(path) : inspector.getState()
      return { content: [{ type: "text", text: JSON.stringify(result, null, 2) }] }
    },
  })

  mc.registerTool({
    name: "debug_evaluate_expression",
    description: "Evaluate a #{...} expression and return result with cache-hit info",
    inputSchema: {
      type: "object",
      properties: {
        expression: { type: "string", description: "Expression (e.g., '#{appData.cart.items.length}')" },
      },
      required: ["expression"],
    },
    readOnly: true,
    async execute(params) {
      const result = inspector.evaluateExpression(params.expression as string)
      return { content: [{ type: "text", text: JSON.stringify(result, null, 2) }] }
    },
  })

  mc.registerTool({
    name: "debug_flow_traces",
    description: "Get recent ActionFlow execution traces with step-level state diffs",
    inputSchema: {
      type: "object",
      properties: {
        limit: { type: "number", description: "Max traces to return (default: 10)" },
      },
    },
    readOnly: true,
    async execute(params) {
      const limit = (params.limit as number) ?? 10
      const traces = inspector.getFlowTraces().slice(-limit)
      return { content: [{ type: "text", text: JSON.stringify(traces, null, 2) }] }
    },
  })

  mc.registerTool({
    name: "debug_cache_stats",
    description: "Get expression cache performance statistics",
    inputSchema: { type: "object", properties: {} },
    readOnly: true,
    async execute() {
      const stats = inspector.getCacheStats()
      return { content: [{ type: "text", text: JSON.stringify(stats, null, 2) }] }
    },
  })
}
